// src/store/deals/deals.slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DealsByStatusState, DealsState } from "./deals.types";
import { TDeal, TDealsFilters, TDealsResponse } from "@/models/deals.model";
import { createDeal, deleteDeal } from "./deals.thunks";

type InitStatusPayload = {
    pipelineId: number;
    statusId: number;
    pageSize: number;
};

type FetchDealsSuccessPayload = {
    pipelineId: number;
    statusId: number;
    response: TDealsResponse;
};

type FetchDealsErrorPayload = {
    pipelineId: number;
    statusId: number;
    error: string;
};

type UpdateDealOptimisticPayload = {
    pipelineId: number;
    dealId: number;
    updates: Partial<TDeal>;
};

type UpdateDealRollbackPayload = {
    pipelineId: number;
    dealId: number;
    previousState: Partial<TDeal>;
};

const DEFAULT_PAGE_SIZE = 20;

const createEmptyStatusState = (pageSize: number): DealsByStatusState => ({
    ids: [],
    page: 0,
    pageSize,
    totalPages: 0,
    totalCount: 0,
    loading: false,
    initialized: false,
});

const initialDealsState: DealsState = {
    initialized: false,
    filters: {},
    entities: {},
    byPipeline: {},
};


const dealsSlice = createSlice({
    name: "deals",
    initialState: initialDealsState,
    reducers: {
        resetDealsState: () => initialDealsState,

        initStatusColumn(state, action: PayloadAction<InitStatusPayload>) {
            const { pipelineId, statusId, pageSize } = action.payload;

            if (!state.byPipeline[pipelineId]) {
                state.byPipeline[pipelineId] = { byStatus: {} };
            }

            if (!state.byPipeline[pipelineId].byStatus[statusId]) {
                state.byPipeline[pipelineId].byStatus[statusId] =
                    createEmptyStatusState(pageSize);
            }
        },

        fetchDealsStart(
            state,
            action: PayloadAction<{ pipelineId: number; statusId: number }>
        ) {
            const { pipelineId, statusId } = action.payload;
            const column = state.byPipeline[pipelineId]?.byStatus[statusId];
            if (!column) return;

            column.loading = true;
            column.error = undefined;
        },

        fetchDealsSuccess(
            state,
            action: PayloadAction<FetchDealsSuccessPayload>
        ) {
            const { pipelineId, statusId, response } = action.payload;
            const column = state.byPipeline[pipelineId].byStatus[statusId];
            const { data, meta } = response;

            data.forEach((deal: TDeal) => {
                state.entities[deal.id] = {
                    ...(state.entities[deal.id] ?? {}),
                    ...deal,
                };
            });

            // Add new deal IDs, avoiding duplicates
            const newIds = data.map((deal) => deal.id).filter(id => !column.ids.includes(id));
            column.ids.push(...newIds);
            column.page = meta.page;
            column.pageSize = meta.pageSize;
            column.totalPages = meta.totalPages;
            column.totalCount = meta.totalCount;
            column.loading = false;
            column.initialized = true;
        },

        fetchDealsError(
            state,
            action: PayloadAction<FetchDealsErrorPayload>
        ) {
            const { pipelineId, statusId, error } = action.payload;
            const column = state.byPipeline[pipelineId]?.byStatus[statusId];
            if (!column) return;

            column.loading = false;
            column.error = error;
        },

        dealCreated(state, action: PayloadAction<TDeal>) {
            const deal = action.payload;
            state.entities[deal.id] = deal;

            // Add to status column if initialized
            const pipelineId = deal.pipelineId;
            const statusId = deal.status;
            const column = state.byPipeline[pipelineId]?.byStatus[statusId];

            if (column && column.initialized) {
                // Add to beginning of column
                column.ids.unshift(deal.id);
                column.totalCount += 1;
            }
        },
        dealUpdated(state, action: PayloadAction<UpdateDealOptimisticPayload>) {
            const { pipelineId, dealId, updates } = action.payload;
            const deal = state.entities[dealId];
            if (!deal) return;

            const fromPipelineId = pipelineId;
            const toPipelineId = updates.pipelineId ?? fromPipelineId;
            const fromStatusId = deal.status;
            const toStatusId = updates.status ?? fromStatusId;
            const pipelineChanged = fromPipelineId !== toPipelineId;
            const statusChanged = fromStatusId !== toStatusId;
            const needsColumnMove = pipelineChanged || statusChanged;

            // Update the entity FIRST: the column bookkeeping below bails out
            // when the board hasn't loaded the affected columns (e.g. the deal
            // reached the store via GET /deal/:id on the details page), and
            // that bail-out must not swallow the entity update.
            state.entities[dealId] = { ...deal, ...updates };

            if (needsColumnMove) {
                const fromPipeline = state.byPipeline[fromPipelineId];
                if (!fromPipeline) return;

                const fromColumn = fromPipeline.byStatus[fromStatusId];
                if (!fromColumn) return;

                /* ensure target pipeline/column exist */
                if (!state.byPipeline[toPipelineId]) {
                    state.byPipeline[toPipelineId] = { byStatus: {} };
                }
                const toPipeline = state.byPipeline[toPipelineId];
                if (!toPipeline.byStatus[toStatusId]) {
                    toPipeline.byStatus[toStatusId] = createEmptyStatusState(DEFAULT_PAGE_SIZE);
                }
                const toColumn = toPipeline.byStatus[toStatusId];

                fromColumn.ids = fromColumn.ids.filter(id => id !== dealId);
                fromColumn.totalCount = Math.max(0, fromColumn.totalCount - 1);

                toColumn.ids.push(dealId);
                toColumn.totalCount += 1;
                toColumn.ids.sort((a, b) => {
                    const dealA = state.entities[a];
                    const dealB = state.entities[b];
                    if (!dealA || !dealB) return 0;
                    return new Date(dealB.created).getTime() - new Date(dealA.created).getTime();
                });
            }
        },

        updateDealRollback(
            state,
            action: PayloadAction<UpdateDealRollbackPayload>
        ) {
            const { dealId, previousState } = action.payload;
            const deal = state.entities[dealId];
            if (!deal) return;

            const currentPipelineId = deal.pipelineId;
            const previousPipelineId = previousState.pipelineId ?? currentPipelineId;
            const currentStatusId = deal.status;
            const previousStatusId = previousState.status ?? currentStatusId;
            const pipelineChanged = currentPipelineId !== previousPipelineId;
            const statusChanged = currentStatusId !== previousStatusId;
            const needsColumnMove = pipelineChanged || statusChanged;

            if (needsColumnMove) {
                const fromPipeline = state.byPipeline[currentPipelineId];
                if (!fromPipeline) return;

                const fromColumn = fromPipeline.byStatus[currentStatusId];
                if (!fromColumn) return;

                const toPipeline = state.byPipeline[previousPipelineId];
                if (!toPipeline?.byStatus[previousStatusId]) return;
                const toColumn = toPipeline.byStatus[previousStatusId];

                fromColumn.ids = fromColumn.ids.filter(id => id !== dealId);
                fromColumn.totalCount = Math.max(0, fromColumn.totalCount - 1);

                toColumn.ids.push(dealId);
                toColumn.totalCount += 1;
                toColumn.ids.sort((a, b) => {
                    const da = state.entities[a];
                    const db = state.entities[b];
                    if (!da || !db) return 0;
                    return new Date(db.created).getTime() - new Date(da.created).getTime();
                });
            }

            state.entities[dealId] = { ...deal, ...previousState };
        },
        dealsEntitiesUpserted(state, action: PayloadAction<TDeal[]>) {
            action.payload.forEach((deal) => {
                state.entities[deal.id] = {
                    ...(state.entities[deal.id] ?? {}),
                    ...deal,
                };
            });
        },
        applyDealsFilters(state, action: PayloadAction<TDealsFilters>) {
            const currentFilters = state.filters;
            let newFilters = { ...currentFilters, ...action.payload };
            // clear falsy values
            newFilters = Object.fromEntries(Object.entries(newFilters).filter(([, val]) => Boolean(val)));

            // set new filters (extend from existing filters)
            state.filters = newFilters;

            // 2️⃣ reset dataset (single source of truth)
            state.entities = {};
            state.byPipeline = {};
            state.initialized = false;
        },
        resetDealsFilters(state) {
            state.filters = {};
            state.entities = {};
            state.byPipeline = {};
            state.initialized = false;
        },

    },
    extraReducers: (builder) => {
        builder.addCase(createDeal.fulfilled, (state, action) => {
            const deal = action.payload;
            state.entities[deal.id] = deal;

            const pipelineId = deal.pipelineId;
            const statusId = deal.status;

            // Initialize pipeline structure if needed
            if (!state.byPipeline[pipelineId]) {
                state.byPipeline[pipelineId] = { byStatus: {} };
            }

            // Initialize column if it doesn't exist
            if (!state.byPipeline[pipelineId].byStatus[statusId]) {
                state.byPipeline[pipelineId].byStatus[statusId] = {
                    ids: [],
                    page: 1,
                    pageSize: DEFAULT_PAGE_SIZE,
                    totalPages: 1,
                    totalCount: 0,
                    loading: false,
                    initialized: true,
                };
            }

            const column = state.byPipeline[pipelineId].byStatus[statusId];

            // Always add the deal, avoiding duplicates
            if (!column.ids.includes(deal.id)) {
                column.ids.unshift(deal.id);
                column.totalCount += 1;
            }
        });
        builder.addCase(deleteDeal.fulfilled, (state, action) => {
            const dealId = action.payload;
            const deal = state.entities[dealId];
            
            if (!deal) return;
            
            // Remove from entities
            delete state.entities[dealId];
            
            // Remove from pipeline/status column
            const pipelineId = deal.pipelineId;
            const statusId = deal.status;
            
            if (state.byPipeline[pipelineId]?.byStatus[statusId]) {
                const column = state.byPipeline[pipelineId].byStatus[statusId];
                column.ids = column.ids.filter(id => id !== dealId);
                column.totalCount = Math.max(0, column.totalCount - 1);
            }
        });
    }
});

export const {
    resetDealsState,
    initStatusColumn,
    fetchDealsStart,
    fetchDealsSuccess,
    fetchDealsError,
    dealCreated,
    dealUpdated,
    updateDealRollback,
    dealsEntitiesUpserted,
    applyDealsFilters,
    resetDealsFilters,
} = dealsSlice.actions;

export default dealsSlice.reducer;
